import { useState, useEffect, useCallback } from 'react';
import { breakpoints } from '../config/theme';

/**
 * useResponsive Hook
 *
 * Provides responsive utilities for React components.
 * Tracks current breakpoint and provides helper functions.
 */

/**
 * Get current breakpoint based on window width
 */
const getBreakpoint = (width) => {
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
};

/**
 * Main responsive hook
 */
export const useResponsive = () => {
  const [windowSize, setWindowSize] = useState(() => {
    if (typeof window === 'undefined') {
      return { width: 1024, height: 768 };
    }
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let timeoutId = null;

    const handleResize = () => {
      // Debounce resize events
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const breakpoint = getBreakpoint(windowSize.width);

  // Breakpoint checks
  const isMobile = windowSize.width < breakpoints.md;
  const isTablet = windowSize.width >= breakpoints.md && windowSize.width < breakpoints.lg;
  const isDesktop = windowSize.width >= breakpoints.lg;

  // Minimum breakpoint checks (min-width)
  const isSmUp = windowSize.width >= breakpoints.sm;
  const isMdUp = windowSize.width >= breakpoints.md;
  const isLgUp = windowSize.width >= breakpoints.lg;
  const isXlUp = windowSize.width >= breakpoints.xl;

  // Maximum breakpoint checks (max-width)
  const isSmDown = windowSize.width < breakpoints.md;
  const isMdDown = windowSize.width < breakpoints.lg;
  const isLgDown = windowSize.width < breakpoints.xl;

  // Component-specific breakpoints
  const isMatrixCompact = windowSize.width < 950; // Matrix switches to 2-row mode earlier
  const isHeroStacked = windowSize.width < 900;   // Hero tiles stack later than tablet breakpoint

  return {
    // Window dimensions
    width: windowSize.width,
    height: windowSize.height,

    // Current breakpoint
    breakpoint,

    // Device type checks
    isMobile,
    isTablet,
    isDesktop,

    // Min-width checks
    isSmUp,
    isMdUp,
    isLgUp,
    isXlUp,

    // Max-width checks
    isSmDown,
    isMdDown,
    isLgDown,

    // Component-specific breakpoints
    isMatrixCompact,
    isHeroStacked,

    // Breakpoint values for reference
    breakpoints,
  };
};

/**
 * useMediaQuery Hook
 *
 * Check if a specific media query matches
 */
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);

    // Set initial value
    setMatches(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
};

/**
 * useBreakpointValue Hook
 *
 * Returns different values based on current breakpoint
 *
 * @example
 * const columns = useBreakpointValue({ xs: 1, sm: 2, md: 3, lg: 4 });
 */
export const useBreakpointValue = (values) => {
  const { breakpoint } = useResponsive();

  // Order of breakpoints from largest to smallest
  const breakpointOrder = ['xl', 'lg', 'md', 'sm', 'xs'];

  // Find the value for current breakpoint or fall back to smaller breakpoints
  const currentIndex = breakpointOrder.indexOf(breakpoint);

  for (let i = currentIndex; i < breakpointOrder.length; i++) {
    const bp = breakpointOrder[i];
    if (values[bp] !== undefined) {
      return values[bp];
    }
  }

  // If no value found, try from the smallest up
  for (let i = breakpointOrder.length - 1; i >= 0; i--) {
    const bp = breakpointOrder[i];
    if (values[bp] !== undefined) {
      return values[bp];
    }
  }

  return undefined;
};

/**
 * Responsive style helper
 *
 * Returns different style objects based on breakpoint
 *
 * @example
 * const style = useResponsiveStyles({
 *   base: { padding: '8px' },
 *   md: { padding: '16px' },
 *   lg: { padding: '24px' },
 * });
 */
export const useResponsiveStyles = (styles) => {
  const { breakpoint } = useResponsive();

  // Start with base styles
  let result = { ...styles.base };

  // Order of breakpoints
  const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl'];
  const currentIndex = breakpointOrder.indexOf(breakpoint);

  // Apply styles for each breakpoint up to current
  for (let i = 0; i <= currentIndex; i++) {
    const bp = breakpointOrder[i];
    if (styles[bp]) {
      result = { ...result, ...styles[bp] };
    }
  }

  return result;
};

export default useResponsive;
